import type { CreateAgendaSlotItemBulkErrorData } from '@omecare/utils';
import { intlFormat } from 'date-fns/intlFormat';
import { ArrowBigRightDashIcon, EyeIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getReservationStatus } from '@/components/sheets/view-user-agenda-slot-sheet/view-user-agenda-slot-sheet.viewmodel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { slotStatusBadgeClassName } from '@/lib/slot-status';
import { cn } from '@/lib/utils';
import { useCreateAgendaSlotBulkListReportViewModel } from './create-agenda-slot-bulk-list-report.viewmodel';

export const CreateAgendaSlotBulkListReport = ({
  title,
  officeId,
  slotsInformation,
}: {
  title: string;
  officeId: string;
  slotsInformation: CreateAgendaSlotItemBulkErrorData[];
}) => {
  const { t } = useTranslation();

  if (slotsInformation.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <span className="text-muted-foreground text-sm">
          {t('admin.dashboard.agenda-slots-pending.list-empty')}
        </span>
      </div>
    );
  }

  const viewModel = useCreateAgendaSlotBulkListReportViewModel({
    officeId,
  });
  const slotsToDisplay = slotsInformation
    .filter((item) => item.slotIntersected)
    .map((item) => {
      const isWorkSchedule =
        item.slotIntersected?.entityType === 'work-schedule';

      let badgeClassName: string;
      if (isWorkSchedule) {
        badgeClassName = slotStatusBadgeClassName('availability');
      } else {
        // @comment: could improve display by reteving slotIntersected.reserversation (reserversation.status)
        badgeClassName = getReservationStatus({
          reservation: undefined,
        }).badgeClassName;
      }

      return {
        ...item,
        date: intlFormat(item.date, {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
        }),
        period: t(`period-of-day.${item.periodOfTheDay}`),
        slotIntersected: item.slotIntersected
          ? {
              ...item.slotIntersected,
              date: intlFormat(item.date, {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
              }),
              period: t(`period-of-day.${item.slotIntersected.periodOfTheDay}`),
              badgeClassName,
            }
          : null,
      };
    });

  return (
    <Card
      className="md:max-h-[350px] lg:max-h-[450px]"
      x-chunk="dashboard-01-chunk-0"
    >
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="flex flex-col">
          <CardTitle className="font-semibold text-md">{t(title)}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-3">
        <div className="h-40 max-h-80 overflow-y-auto">
          <div className="flex flex-col gap-6">
            {slotsToDisplay.map((item, index) => {
              return (
                <div
                  key={index}
                  className="flex items-center gap-6"
                >
                  <div className="flex flex-1 gap-6">
                    <div className="flex w-2/5 flex-col">
                      <p className="flex-1 font-medium text-sm leading-none">
                        {item.date} {item.period}
                      </p>
                    </div>

                    <ArrowBigRightDashIcon className="h-6 w-6" />

                    {item.slotIntersected && (
                      <div className="flex flex-1 flex-col gap-1">
                        <p className="flex-1 font-medium text-sm leading-none">
                          {item.slotIntersected.date}{' '}
                          {item.slotIntersected.period}
                        </p>

                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">
                            <Badge
                              className={cn(
                                'text-xs',
                                item.slotIntersected.badgeClassName,
                              )}
                              variant="outline"
                            >
                              {t(
                                'create-agenda-slot-bulk-report-modal.create-agenda-slot-bulk-report-error.slot-intersected.' +
                                  item.slotIntersected.entityType,
                              )}
                            </Badge>
                          </span>

                          <p className="text-muted-foreground text-sm">
                            {item.slotIntersected.type}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex-none">
                    <Button
                      type="submit"
                      className="flex-1 gap-2"
                      onClick={() => viewModel.openSlotModal(item)}
                    >
                      <EyeIcon className="h-5 w-5" />
                      <span className="hidden xl:block">
                        {t('btn.view-details')}
                      </span>
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
